import { useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { SessionStore } from '../lib/session/store.js';
import { SessionProvider, useSession, useSessionStore } from '../lib/session/context.js';
import { TenantsApiProvider } from '../lib/api/context.js';
import { createTenantsApi } from '../lib/api/tenants.js';
import { Loader } from '@gravity-ui/uikit';
import { LoginScreen } from '../features/auth/login-screen.js';
import { InstallApp } from '../ui/install-app.js';
import { createAppRouter } from './router.js';

export function App({ session }: { session: SessionStore }) {
  const queryClient = useMemo(() => createQueryClient(), []);

  useEffect(() => {
    void session.restore();
  }, [session]);

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider store={session}>
        <InstallApp />
        <Screen />
      </SessionProvider>
    </QueryClientProvider>
  );
}

function Screen() {
  const session = useSession();

  switch (session.status) {
    case 'restoring':
      return (
        <main className="flex h-full items-center justify-center" aria-busy="true">
          <Loader size="m" />
          <span className="sr-only">Проверяем вход…</span>
        </main>
      );
    case 'email':
    case 'code':
      return <LoginScreen />;
    case 'ready':
      // Ключ на менеджере: вход другим человеком на том же телефоне обязан
      // пересобрать дерево и забыть запросы предыдущего.
      return <Workspace key={session.manager.id} />;
  }
}

function Workspace() {
  const store = useSessionStore();
  const [tenants] = useState(() => createTenantsApi(store.authorizedFetch));
  const [router] = useState(() => createAppRouter());

  return (
    <TenantsApiProvider api={tenants}>
      <RouterProvider router={router} />
    </TenantsApiProvider>
  );
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Менеджер уходит в звонилку и возвращается: свежие сроки важнее
        // экономии одного запроса.
        refetchOnWindowFocus: true,
        retry: 1,
      },
    },
  });
}
